import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Search, MapPin, Plane, Clock, Users } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CheckinPage() {
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!confirmation.trim()) return;
    setLoading(true);
    setBookings(null);
    try {
      const res = await fetch(`${API}/api/pickup-checkin/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation_number: confirmation.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Not found");
      setBookings(data.bookings);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (bookingId) => {
    setConfirming(bookingId);
    try {
      const res = await fetch(`${API}/api/pickup-checkin/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_id: bookingId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success(data.message);
      setBookings((prev) =>
        prev.map((b) =>
          b.id === bookingId ? { ...b, already_checked_in: true } : b
        )
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setConfirming(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 py-6 px-3 sm:py-12 sm:px-4" data-testid="checkin-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-3">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Pickup Check-In
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Confirm your pickup for tonight or tomorrow
          </p>
        </div>

        <Card className="shadow-xl border-0 rounded-2xl">
          <CardHeader className="bg-green-600 text-white rounded-t-2xl pb-4">
            <CardTitle className="text-base font-bold">Enter Your Confirmation Number</CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <form onSubmit={handleLookup} className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Confirmation Number</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    data-testid="checkin-confirmation-input"
                    placeholder="e.g. EASY-4380C or 4380C"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    className="text-base uppercase tracking-wider font-mono"
                  />
                  <Button
                    type="submit"
                    disabled={loading || !confirmation.trim()}
                    className="bg-green-600 hover:bg-green-700 px-4 shrink-0"
                    data-testid="checkin-lookup-btn"
                  >
                    {loading ? "..." : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Find your confirmation number in your booking email or WhatsApp message
                </p>
              </div>
            </form>

            {bookings && bookings.length > 0 && (
              <div className="mt-6 space-y-4" data-testid="checkin-results">
                {bookings.map((b) => (
                  <div
                    key={b.id}
                    className={`rounded-xl border-2 p-4 transition-colors ${
                      b.already_checked_in
                        ? "border-green-300 bg-green-50"
                        : "border-slate-200 bg-white"
                    }`}
                    data-testid={`checkin-booking-${b.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-bold text-slate-900 text-lg">{b.full_name}</p>
                        <p className="text-xs text-slate-500 font-mono">{b.confirmation}</p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                        b.trip_type === "return"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-green-100 text-green-700"
                      }`}>
                        {b.trip_type === "return" ? "Return" : "Arrival"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span>{b.travel_date} at {b.travel_time || "TBD"}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        <span>{b.num_passengers} passenger{b.num_passengers > 1 ? "s" : ""}</span>
                      </div>
                      <div className="flex items-start gap-1.5 text-slate-600 col-span-2">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                        <span>{b.pickup_location}</span>
                      </div>
                      {b.flight_number && (
                        <div className="flex items-center gap-1.5 text-slate-600 col-span-2">
                          <Plane className="w-3.5 h-3.5 text-slate-400" />
                          <span>{b.flight_number}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4">
                      {b.already_checked_in ? (
                        <div className="flex items-center gap-2 text-green-700 font-semibold text-sm" data-testid={`checkin-confirmed-${b.id}`}>
                          <CheckCircle className="w-5 h-5" />
                          Pickup Confirmed — see you soon!
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleConfirm(b.id)}
                          disabled={confirming === b.id}
                          className="w-full bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl h-11"
                          data-testid={`checkin-confirm-btn-${b.id}`}
                        >
                          {confirming === b.id ? "Confirming..." : "Confirm My Pickup"}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          Easy Transportation — Easy Ride, Exceptional Experience
        </p>
      </div>
    </div>
  );
}
